import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  TextField
} from '@mui/material';
import {
  Download,
  PictureAsPdf,
  Description,
  DateRange
} from '@mui/icons-material';
import enGB from 'date-fns/locale/en-GB'; 
import { Calendar, DollarSign, Activity, Heart, Clock } from 'lucide-react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { exportAttendanceToExcel, exportAttendanceToWord } from '../utils/reportExportUtils';
// Components
import AbsenceAccordion from '../components/accordions/AbsenceAccordion';

// API Services
import { getAttendanceReport, getAttendanceSummary, getEmployeesWithHighAbsence, 
    getYearlyAttendanceSummary, getEmployeeAttendanceReport  } from '../services/reportingAPIs';

// ✅ Accepts onCardClick prop
const WeekAttendance = ({startDate, endDate, loading, onCardClick}) => {
  const componentRef = useRef();
  const [data, setData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [yearSummary, setYearSummary] = useState([]);
  const [highAbsences, setHighAbsences] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [loading1, setLoading1] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });

  const formatDateForBackend = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleDateString('en-CA'); 
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'Attendance Report',
    pageStyle: `
    @page { size: landscape; }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .attendanceReportContainer {
          padding:3,
        }
      }
    `
  });

  const handleExportExcel = () => {
    exportAttendanceToExcel(data, dateRange);
  };

  const handleExportWord = () => {
    exportAttendanceToWord(data, dateRange);
  };

  const lastFetchedRangeRef = useRef('');

  useEffect(() => {
    const from = formatDateForBackend(startDate || dateRange.from);
    const to = formatDateForBackend(endDate || dateRange.to);
    const rangeKey = `${from}_${to}`;
    if (!from || !to || lastFetchedRangeRef.current === rangeKey) return;
    lastFetchedRangeRef.current = rangeKey;
    fetchData();
  }, [dateRange.from, dateRange.to, startDate, endDate]); // Added startDate/endDate deps

  const fetchData = async () => {
    try {
      setLoading1(true);
      setError(null);
      
      const formatDateForBackend = (date) => {
        if (!date) return null;
        return new Date(date).toLocaleDateString('en-CA'); 
      };
      
      // Use props if available, otherwise internal state
      const from = formatDateForBackend(startDate || dateRange.from);
      const to = formatDateForBackend(endDate || dateRange.to);

      if(!from || !to) { // Initial load safety if no dates passed
          setLoading1(false);
          return; 
      }

      const result = await getAttendanceReport(from, to);
      const summary = await getAttendanceSummary(from, to);
      const highAbsenceData = await getEmployeesWithHighAbsence(from, to);
      const yearData = await getYearlyAttendanceSummary();
      setSummaryData(summary || {});
      setData(result || []);
      setHighAbsences(highAbsenceData || []);
      setYearSummary(yearData);
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading1(false);
    }
  };

  const filteredData = useMemo(() => {
    if (activeFilter === 'ALL') {
      return data; 
    }
    return data.filter(employee => {
      if (['ANNUAL_LEAVE', 'UNPAID_LEAVE'].includes(activeFilter)) {
        return employee.leaveDays[activeFilter] > 0;
      } else if (activeFilter === 'SICKNESS') {
        return employee.totalSicknessRecords > 0;
      } else if (activeFilter === 'LATENESS') {
        return employee.totalLatenessRecords > 0;
      } else if (activeFilter === 'PERSONAL') {
        return employee.totalPersonalRecords > 0;
      }
      return false;
    });
  }, [data, activeFilter]);

  const formatLatenessHours = (hours) => {
    if (!hours || hours <= 0) return '0 min';
    if (hours >= 1) return `${hours.toFixed(1)} hrs`;
    return `${Math.round(hours * 60)} min`;
  };

  // ✅ Interactive Card Handler
  const handleCardClick = (type) => {
    // If a parent handler is provided (like in HRWeeklyReport), use it
    if (onCardClick) {
        onCardClick(type);
    } else {
        // Otherwise fallback to internal filter (for standalone use)
        setActiveFilter(type);
    }
  };

  if (loading || loading1) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB} >
      <Box ref={componentRef} className='attendanceReportContainer'>
        
        {/* ✅ SUMMARY CARDS */}
        <Grid container sx={{width:'100%'}} spacing={3}>
          {/* Annual Leave */}
          <Grid item sx={{ flex: 1 }}>
            <Card 
              onClick={() => handleCardClick('ANNUAL_LEAVE')} // ✅ Clickable
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(102, 126, 234, 0.3)'
                }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>Annual Leave</Typography>
                    <Typography variant="h4" fontWeight="bold">{summaryData.annualLeaveDays || 0}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>days</Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Calendar size={24} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">{formatLatenessHours(summaryData.annualLeaveHours)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Unpaid Leave */}
          <Grid item xs sx={{ flex: 1 }}>
            <Card 
              onClick={() => handleCardClick('UNPAID_LEAVE')} // ✅ Clickable
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #8697c4 0%, #7091e6 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>Unpaid Leave</Typography>
                    <Typography variant="h4" fontWeight="bold">{summaryData.unpaidLeaveDays || 0}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>days</Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DollarSign size={24} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">{formatLatenessHours(summaryData.unpaidLeaveHours)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sickness */}
          <Grid item xs sx={{ flex: 1 }}>
            <Card 
              onClick={() => handleCardClick('SICKNESS')} // ✅ Clickable
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #2196f3 0%, #adbbda 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(250, 112, 154, 0.3)' }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>Sickness</Typography>
                    <Typography variant="h4" fontWeight="bold">{summaryData.sicknessRecords || 0}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>records</Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Activity size={24} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">{formatLatenessHours(summaryData.sicknessHours)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Personal Commitment */}
          <Grid item xs sx={{ flex: 1 }}>
            <Card 
              onClick={() => handleCardClick('PERSONAL')} // ✅ Clickable
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(161, 140, 209, 0.3)' }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>Personal Commitment</Typography>
                    <Typography variant="h4" fontWeight="bold">{summaryData.personalRecords || 0}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>records</Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Heart size={24} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">{formatLatenessHours(summaryData.personalHours)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Lateness */}
          <Grid item xs sx={{ flex: 1 }}>
            <Card 
              onClick={() => handleCardClick('LATENESS')} // ✅ Clickable
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(79, 172, 254, 0.3)' }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>Lateness</Typography>
                    <Typography variant="h4" fontWeight="bold">{summaryData.latenessRecords || 0}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>records</Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Clock size={24} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">{formatLatenessHours(summaryData.latenessHours)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <AbsenceAccordion highAbsences={highAbsences} number={2} sickN={2} loading={loading}/>

      </Box>
    </LocalizationProvider>
  );
};

export default WeekAttendance;